import { ChevronLeft, ChevronRight, MoreVertical, Pencil } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Pill, TBody, TD, TH, THead, TR, Table } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";
import type {
  StockProduct,
  StockStatus,
} from "@/repositories/catalogueRepository";
import { useAuthStore } from "@/stores/authStore";

import { ProductForm } from "./ProductForm";
import { StockDialog, type StockAction } from "./StockDialog";
import { ProductTile } from "./panels";

const STATUS: Record<
  StockStatus,
  { label: string; tone: "success" | "warn" | "danger" }
> = {
  IN_STOCK: { label: "In stock", tone: "success" },
  LOW_STOCK: { label: "Low stock", tone: "warn" },
  OUT_OF_STOCK: { label: "Out of stock", tone: "danger" },
};

export function ProductTable({
  products,
  total,
  page,
  pageSize,
  onPage,
}: {
  products: StockProduct[];
  total: number;
  page: number;
  pageSize: number;
  onPage: (page: number) => void;
}) {
  const canSeeCost = useAuthStore((s) => s.can("products.view_cost_price"));

  return (
    <>
      <Table>
        {/* No `<TR>` inside `<THead>`. THead emits its own `<tr>`, so wrapping the
            header cells in another one nests `<tr>` inside `<tr>` — invalid HTML
            that the browser silently repairs by hoisting the inner row out of the
            table, at which point the header cells stop participating in the
            column widths and the headings drift out of line with their columns.
            Every other table in the app does it this way; this one did not. */}
        <THead>
          {/* On a phone this narrows to the product, what is on the shelf and
              what to do about it. Everything dropped is reachable by opening
              the row — see `hideBelow` on `TH`. */}
          <TH className="min-w-[12rem] sm:min-w-[14rem]">Product</TH>
          <TH hideBelow="md">Category</TH>
          {canSeeCost && (
            <TH hideBelow="lg" className="text-right">
              Cost price
            </TH>
          )}
          <TH hideBelow="sm" className="text-right">
            Selling price
          </TH>
          <TH className="text-right">Stock</TH>
          <TH hideBelow="sm">Status</TH>
          <TH hideBelow="xl">Last updated</TH>
          <TH className="text-right">Actions</TH>
        </THead>
        <TBody>
          {products.map((product) => (
            <ProductRow
              key={product.id}
              product={product}
              canSeeCost={canSeeCost}
            />
          ))}
        </TBody>
      </Table>

      <Pagination
        total={total}
        page={page}
        pageSize={pageSize}
        onPage={onPage}
      />
    </>
  );
}

function ProductRow({
  product,
  canSeeCost,
}: {
  product: StockProduct;
  canSeeCost: boolean;
}) {
  const status = STATUS[product.stock_status];
  const onHand = Number(product.stock_on_hand);
  const [action, setAction] = useState<StockAction | null>(null);
  const [editing, setEditing] = useState(false);

  return (
    <TR>
      <TD>
        <div className="flex items-center gap-3">
          <ProductTile name={product.name} src={product.image_thumb_url} />
          <div className="min-w-0">
            <div className="truncate font-medium text-ink-900">
              {product.name}
            </div>
            <div className="truncate font-mono text-xs text-ink-500">
              {product.barcodes[0]?.barcode ?? product.sku ?? "—"}
            </div>
          </div>
        </div>
      </TD>

      <TD hideBelow="md">
        {product.category_name ? (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm">
            <span
              className="size-2 rounded-full"
              style={{ backgroundColor: product.category_colour || "#94a3b8" }}
              aria-hidden
            />
            {product.category_name}
          </span>
        ) : (
          <span className="text-ink-400">—</span>
        )}
      </TD>

      {canSeeCost && (
        <TD hideBelow="lg" className="text-right tabular-nums">
          {product.cost_price && Number(product.cost_price) > 0 ? (
            formatMoney(product.cost_price)
          ) : (
            <span className="text-ink-400">—</span>
          )}
        </TD>
      )}

      <TD hideBelow="sm" className="text-right tabular-nums">
        {formatMoney(product.selling_price)}
      </TD>

      <TD className="text-right">
        <div className="font-semibold tabular-nums text-ink-900">{onHand}</div>
        <div className="text-xs text-ink-500">
          {product.unit?.plural_name ?? "Units"}
        </div>
      </TD>

      <TD hideBelow="sm">
        <Pill tone={status.tone}>{status.label}</Pill>
      </TD>

      <TD hideBelow="xl" className="whitespace-nowrap text-sm text-ink-500">
        {new Date(product.updated_at).toLocaleDateString("en-ZA", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })}
        <div className="text-xs">
          {new Date(product.updated_at).toLocaleTimeString("en-ZA", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </TD>

      <TD>
        <RowActions
          product={product}
          onAction={setAction}
          onEdit={() => setEditing(true)}
        />
        {action && (
          <StockDialog
            action={action}
            product={product}
            onClose={() => setAction(null)}
          />
        )}
        {editing && (
          <ProductForm product={product} onClose={() => setEditing(false)} />
        )}
      </TD>
    </TR>
  );
}

/**
 * The pencil and the kebab from the design.
 *
 * The menu items write to the stock ledger, so which of them appear depends on
 * the caller's permissions — a cashier who may look at stock but not receive it
 * sees only "View movements", rather than a list of things that would 403.
 *
 * The pencil edits everything except the price; changing what a customer pays is
 * a different act, needing a reason and an author, so it lives inside that form
 * in its own section and goes through its own endpoint.
 */
function RowActions({
  product,
  onAction,
  onEdit,
}: {
  product: StockProduct;
  onAction: (action: StockAction) => void;
  onEdit: () => void;
}) {
  const can = useAuthStore((s) => s.can);
  const canEdit = can("products.edit");
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  const items = (
    [
      {
        label: "Receive stock",
        action: "receive",
        allowed: can("inventory.receive_stock"),
      },
      {
        label: "Adjust stock",
        action: "adjust",
        allowed: can("inventory.adjust_stock"),
      },
      {
        label: "Count stock",
        action: "count",
        allowed: can("inventory.stock_count"),
      },
      {
        label: "View movements",
        action: "movements",
        allowed: can("inventory.view"),
      },
    ] satisfies { label: string; action: StockAction; allowed: boolean }[]
  ).filter((item) => item.allowed);

  useEffect(() => {
    if (!open) return;

    function dismiss(event: MouseEvent) {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    }
    function escape(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <div
      ref={container}
      className="relative flex items-center justify-end gap-1"
    >
      <button
        type="button"
        onClick={onEdit}
        disabled={!canEdit}
        title={canEdit ? "Edit this product" : "You may not edit products"}
        aria-label={`Edit ${product.name}`}
        className="grid size-9 place-items-center rounded-control border border-line text-ink-500 hover:text-ink-900 disabled:opacity-40"
      >
        <Pencil className="size-4" aria-hidden />
      </button>

      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        disabled={items.length === 0}
        aria-label={`More actions for ${product.name}`}
        aria-expanded={open}
        aria-haspopup="menu"
        className={cn(
          "grid size-9 place-items-center rounded-control border border-line text-ink-500",
          "hover:text-ink-900 disabled:opacity-40",
          open && "bg-canvas text-ink-900",
        )}
      >
        <MoreVertical className="size-4" aria-hidden />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-10 z-20 w-52 rounded-card border border-line bg-surface p-1 shadow-card"
        >
          {items.map((item) => (
            <button
              key={item.action}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onAction(item.action);
              }}
              className="block w-full rounded-control px-3 py-2 text-left text-sm text-ink-600 hover:bg-canvas hover:text-ink-900"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Pagination({
  total,
  page,
  pageSize,
  onPage,
}: {
  total: number;
  page: number;
  pageSize: number;
  onPage: (page: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
      <p className="text-sm text-ink-500">
        Showing <span className="font-medium text-ink-900">{first}</span>–
        <span className="font-medium text-ink-900">{last}</span> of{" "}
        <span className="font-medium text-ink-900">{total}</span>
      </p>

      <div className="flex items-center gap-1">
        <PageButton
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          label="Previous page"
        >
          <ChevronLeft className="size-4" aria-hidden />
        </PageButton>

        {pageNumbers(page, pages).map((entry, index) =>
          entry === null ? (
            // Keyed by position: two gaps in one list are legitimately identical,
            // and a shared key would collide.
            <span key={`gap-${index}`} className="px-1.5 text-ink-400">
              …
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              onClick={() => onPage(entry)}
              aria-current={entry === page ? "page" : undefined}
              className={cn(
                "h-9 min-w-9 rounded-control px-2 text-sm tabular-nums",
                entry === page
                  ? "bg-brand-500 font-semibold text-white"
                  : "border border-line text-ink-600 hover:text-ink-900",
              )}
            >
              {entry}
            </button>
          ),
        )}

        <PageButton
          onClick={() => onPage(page + 1)}
          disabled={page >= pages}
          label="Next page"
        >
          <ChevronRight className="size-4" aria-hidden />
        </PageButton>
      </div>
    </div>
  );
}

function PageButton({
  onClick,
  disabled,
  label,
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="grid size-9 place-items-center rounded-control border border-line text-ink-600 hover:text-ink-900 disabled:opacity-40"
    >
      {children}
    </button>
  );
}

/**
 * Page numbers with gaps: 1 … 4 5 6 … 20.
 *
 * A merchant with 40 000 products has 2 000 pages, and rendering every button
 * would be a two-thousand-element list nobody can use.
 */
function pageNumbers(current: number, total: number): (number | null)[] {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);

  const entries = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...entries]
    .filter((page) => page >= 1 && page <= total)
    .sort((a, b) => a - b);

  const result: (number | null)[] = [];
  let previous = 0;
  for (const page of sorted) {
    if (page - previous > 1) result.push(null);
    result.push(page);
    previous = page;
  }
  return result;
}
